import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';
import SpaRedirect from '@/components/seo/SpaRedirect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/seo/blog/posts/slug/${slug}`);
      setPost(response.data);
      
      // Fetch related posts from same topic
      const relatedResponse = await axios.get(
        `${BACKEND_URL}/api/seo/blog/posts?status=published&topic=${response.data.topic}&limit=3`
      );
      setRelatedPosts(relatedResponse.data.posts.filter(p => p.slug !== slug).slice(0, 2));
    } catch (err) {
      // Only 404 = genuinely missing -> noindex. Transient (5xx/network) must NOT noindex.
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('transient');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    // Definitive 404 only (deleted/nonexistent post) -> clean redirect to the
    // blog index instead of a 200+noindex soft-404. Google reclassifies the dead
    // URL as "Page with redirect" and drops it fast.
    return <SpaRedirect to="/blogs" message="Redirecting to our blog…" />;
  }

  if (!post) {
    // Transient error: keep neutral loading (NO noindex) so Google retries.
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.meta_title}</title>
        <meta name="description" content={post.meta_description} />
        <meta property="og:title" content={post.meta_title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:type" content="article" />
        <meta name="keywords" content={post.keywords?.join(', ')} />
        <link rel="canonical" href={`https://fortworthdental.com/blogs/${post.slug}/`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Link to="/blog" className="text-teal-200 hover:text-white mb-4 inline-block">
                ← Back to Blog
              </Link>
              <div className="flex items-center text-sm mb-4">
                <span className="bg-white/20 px-3 py-1 rounded-full">{post.topic}</span>
                <span className="mx-3">•</span>
                <span>{new Date(post.published_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">{post.title}</h1>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                  <span className="text-xl font-bold">{post.author?.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-semibold">{post.author}</p>
                  <p className="text-sm text-teal-200">{post.author_credentials}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            {/* Article Content */}
            <article className="bg-white rounded-lg shadow-md p-8 mb-12">
              <div 
                className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-a:text-teal-600"
                dangerouslySetInnerHTML={{ 
                  __html: post.content
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n## (.*?)\n/g, '<h2 class="text-2xl font-bold mt-8 mb-4">$1</h2>')
                    .replace(/\n### (.*?)\n/g, '<h3 class="text-xl font-bold mt-6 mb-3">$1</h3>')
                    .replace(/\n- (.*?)(?=\n|$)/g, '<li class="ml-4">$1</li>')
                    .replace(/\n\n/g, '</p><p class="mb-4">')
                    .replace(/^\s*/, '<p class="mb-4">')
                    .replace(/\s*$/, '</p>')
                }}
              />
            </article>

            {/* Author Box */}
            <div className="bg-teal-50 rounded-lg p-6 mb-12">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">{post.author?.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{post.author}</h3>
                  <p className="text-teal-600 mb-2">{post.author_credentials}</p>
                  <p className="text-gray-600">
                    {post.author === 'Dr. Mund' 
                      ? 'Dr. Mund has over 25 years of experience in dental implant dentistry and has been recognized 13 times as a "Top Dentist" by Fort Worth Texas Magazine.'
                      : 'Dr. Razo is an experienced general and cosmetic dentist dedicated to helping patients achieve beautiful, healthy smiles with personalized care.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Related Articles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {relatedPosts.map((relatedPost) => (
                    <Link 
                      key={relatedPost.id} 
                      to={`/blogs/${relatedPost.slug}`}
                      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                      <span className="text-sm text-teal-600 font-semibold">{relatedPost.topic}</span>
                      <h3 className="text-lg font-bold text-gray-800 mt-2 hover:text-teal-600">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-600 text-sm mt-2">By {relatedPost.author}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 rounded-lg text-center mb-12">
              <h2 className="text-2xl font-bold mb-4">Schedule Your Consultation</h2>
              <p className="mb-6">Have questions about {post.topic.toLowerCase()}? {post.author} is here to help!</p>
              <a 
                href="tel:8179853337" 
                className="bg-white text-teal-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 inline-block"
              >
                Call (817) 985-3337
              </a>
            </section>

            {/* Appointment Form */}
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Book Your Appointment
              </h2>
              <AppointmentForm />
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
